import React, { useState, useEffect } from 'react';
import { Gift, Loader2, CheckCircle2, Copy, Check, Users, MessageCircle, Mail, Send } from 'lucide-react';
import { API_URL } from '../config/api';
import { toast } from 'sonner';

const PAISES = [
  "Argentina","Bolivia","Brasil","Canada","Chile","Colombia","Costa Rica","Cuba","Ecuador",
  "El Salvador","Estados Unidos","Guatemala","Honduras","Mexico",
  "Nicaragua","Panama","Paraguay","Peru","Puerto Rico","Republica Dominicana",
  "Uruguay","Venezuela","Otro"
];

const CertificadosPage = () => {
  const [codigos, setCodigos] = useState([]);
  const [stats, setStats] = useState({ total: 0, disponibles: 0, reclamados: 0 });
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '', pais: '' });
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const [agenteNombre, setAgenteNombre] = useState('');

  const [loadError, setLoadError] = useState(false);

  const loadCodigos = async () => {
    const MAX_RETRIES = 3;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const res = await fetch(`${API_URL}/api/codigos-viaje/disponibles`, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          setCodigos(data.codigos);
          setStats({ total: data.total, disponibles: data.disponibles, reclamados: data.reclamados });
          setLoadError(false);
          setLoading(false);
          return;
        }
        if (res.status < 500) break;
      } catch (err) {
        console.error(`Load codigos attempt ${attempt + 1} failed:`, err);
      }
      if (attempt < MAX_RETRIES - 1) {
        await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
    setLoadError(true);
    setLoading(false);
  };

  useEffect(() => { loadCodigos(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref') || params.get('agente');
    if (!ref) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/agentes/${encodeURIComponent(ref)}`);
        if (res.ok) {
          const data = await res.json();
          if (data?.nombre) setAgenteNombre(data.nombre);
        }
      } catch (err) {
        console.error('Load agente failed:', err);
      }
    })();
  }, []);

  const handleReclamar = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.nombre || !form.email || !form.telefono || !form.pais) {
      setError('Completa todos los campos para reclamar tu codigo.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('El correo electronico no es valido.');
      return;
    }

    setClaiming(prev => ({ ...prev, loading: true }));
    try {
      const res = await fetch(`${API_URL}/api/codigos-viaje/reclamar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codigo_id: claiming.id, ...form })
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setResult(data);
        toast.success('Codigo reclamado exitosamente!');
      } else {
        setError(data.detail || 'Error al reclamar el codigo.');
        if (res.status === 410) loadCodigos();
      }
    } catch {
      setError('Error de conexion. Intenta de nuevo.');
    }
    setClaiming(prev => prev ? { ...prev, loading: false } : null);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result.codigo);
    setCopied(true);
    toast.success('Codigo copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  const getWhatsAppUrl = () => {
    if (!result) return '#';
    const msg = encodeURIComponent(
      `Hola ${result.nombre}! 🎉\n\n` +
      `Te han asignado un *Codigo de Viaje de $500 USD* de Club Viajemos.\n\n` +
      `🎫 Tu codigo: *${result.codigo}*\n` +
      `💰 Valor: $500 USD en beneficios hoteleros\n\n` +
      `Para redimirlo, visita:\n` +
      `👉 https://www.clubviajemos.com/redimir\n\n` +
      `Ingresa tu codigo y sigue las instrucciones.\n\n` +
      `Club Viajemos — Travel Empire`
    );
    const phone = result.telefono ? result.telefono.replace(/[\s\-\(\)]/g, '') : '';
    return `https://wa.me/${phone}?text=${msg}`;
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      const res = await fetch(`${API_URL}/api/codigos-viaje/enviar-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: result.email, nombre: result.nombre, codigo: result.codigo })
      });
      if (res.ok) {
        setEmailSent(true);
        toast.success(`Instrucciones enviadas a ${result.email}`);
      } else {
        toast.error('Error al enviar email');
      }
    } catch {
      toast.error('Error de conexion');
    }
    setSendingEmail(false);
  };

  const handleNew = () => {
    setResult(null);
    setClaiming(null);
    setForm({ nombre: '', email: '', telefono: '', pais: '' });
    setError('');
    setEmailSent(false);
    loadCodigos();
  };

  return (
    <div className="min-h-screen" style={{ background: '#1A1A2E', fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <header className="border-b-2 border-[#B8860B] py-8 text-center" style={{ background: 'linear-gradient(180deg, #0d0d1e 0%, #1A1A2E 100%)' }}>
        <p className="text-[11px] tracking-[3px] text-[#D4AF37] mb-2" style={{ fontFamily: 'monospace' }}>Certificados de Viaje</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-wide" style={{ color: '#FDFAF3' }}>
          CLUB <span className="text-[#D4AF37]">VIAJEMOS</span>
        </h1>
        <p className="text-xs tracking-[2px] text-[#888] mt-2">C O D I G O S &nbsp; D E &nbsp; V I A J E &nbsp; $ 5 0 0 &nbsp; U S D</p>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        {/* Agent banner */}
        {agenteNombre && (
          <div className="text-center mb-6 pb-4 border-b" style={{ borderColor: 'rgba(212,175,55,0.2)' }}>
            <p className="text-[10px] tracking-[3px] text-[#888] mb-1" style={{ fontFamily: 'monospace' }}>AGENTE ASIGNADO</p>
            <p className="text-2xl font-bold" style={{ color: '#D4AF37' }} data-testid="agente-nombre">
              {agenteNombre}
            </p>
          </div>
        )}

        {/* Stats */}
        <div className="flex justify-center gap-6 mb-8">
          <div className="text-center">
            <p className="text-2xl font-bold text-[#D4AF37]">{stats.disponibles}</p>
            <p className="text-[10px] text-[#888] uppercase tracking-wider">Disponibles</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-[#6de89a]">{stats.reclamados}</p>
            <p className="text-[10px] text-[#888] uppercase tracking-wider">Reclamados</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-[#FDFAF3]">{stats.total}</p>
            <p className="text-[10px] text-[#888] uppercase tracking-wider">Total</p>
          </div>
        </div>

        {/* SUCCESS STATE */}
        {result ? (
          <div className="border" style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(212,175,55,0.25)' }}>
            <div className="text-center py-6 px-8 border-b" style={{ background: 'rgba(30,132,73,0.12)', borderColor: 'rgba(30,132,73,0.3)' }}>
              <CheckCircle2 className="h-10 w-10 mx-auto mb-2" style={{ color: '#6de89a' }} />
              <h3 className="text-xl font-bold tracking-wider" style={{ color: '#6de89a' }}>CODIGO RECLAMADO</h3>
              <p className="text-sm text-[#888] mt-1">Asignado a {result.nombre}</p>
            </div>
            <div className="px-8 py-6 text-center">
              <p className="text-[10px] text-[#888] uppercase tracking-wider mb-2">Tu codigo de $500 USD</p>
              <div
                onClick={handleCopy}
                className="inline-flex items-center gap-3 px-6 py-4 cursor-pointer transition-colors"
                style={{ background: 'rgba(212,175,55,0.1)', border: '2px dashed rgba(212,175,55,0.4)' }}
                data-testid="copy-codigo"
              >
                <span className="text-2xl font-mono font-bold text-[#D4AF37] tracking-wider">{result.codigo}</span>
                {copied ? <Check className="h-5 w-5 text-[#6de89a]" /> : <Copy className="h-5 w-5 text-[#D4AF37]" />}
              </div>
              <p className="text-xs text-[#888] mt-3">Haz click para copiar</p>
            </div>

            {/* Send to client */}
            <div className="px-8 pb-6">
              <p className="text-[10px] text-[#888] uppercase tracking-wider mb-3 text-center">Enviar codigo al cliente</p>
              <div className="flex flex-col sm:flex-row gap-2.5">
                <a
                  href={getWhatsAppUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="send-whatsapp"
                  className="flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-bold tracking-wide transition-colors cursor-pointer"
                  style={{ background: '#25D366', color: '#fff', border: 'none' }}
                >
                  <MessageCircle className="h-5 w-5" />
                  Enviar por WhatsApp
                </a>
                <button
                  onClick={handleSendEmail}
                  disabled={sendingEmail || emailSent}
                  data-testid="send-email"
                  className="flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-bold tracking-wide transition-colors cursor-pointer disabled:opacity-60"
                  style={{ background: emailSent ? 'rgba(30,132,73,0.3)' : '#3b82f6', color: '#fff', border: 'none' }}
                >
                  {sendingEmail ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Enviando...</>
                  ) : emailSent ? (
                    <><Check className="h-5 w-5" /> Email Enviado</>
                  ) : (
                    <><Mail className="h-5 w-5" /> Enviar por Email</>
                  )}
                </button>
              </div>
              {emailSent && (
                <p className="text-xs text-center mt-2" style={{ color: '#6de89a' }}>
                  Instrucciones enviadas a {result.email}
                </p>
              )}
              <p className="text-[10px] text-[#888] text-center mt-3">
                El cliente podra redimir su codigo en <span className="text-[#D4AF37]">clubviajemos.com/redimir</span>
              </p>
            </div>

            <div className="px-8 pb-6 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <button
                onClick={handleNew}
                className="w-full mt-4 py-3 text-sm tracking-wide text-[#888] cursor-pointer border hover:text-[#FDFAF3] transition-colors"
                style={{ background: 'transparent', borderColor: 'rgba(255,255,255,0.1)' }}
              >
                Reclamar otro codigo
              </button>
            </div>
          </div>
        ) : claiming ? (
          /* CLAIM FORM */
          <div>
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 mb-3" style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.3)' }}>
                <Gift className="h-4 w-4 text-[#D4AF37]" />
                <span className="text-[#D4AF37] font-mono text-sm font-bold">{claiming.codigo}</span>
                <span className="text-[#888] text-xs">— $500 USD</span>
              </div>
              <h2 className="text-xl font-bold mb-1" style={{ color: '#FDFAF3' }}>Datos del Cliente</h2>
              <p className="text-sm text-[#888]">Ingresa los datos de quien usara este certificado</p>
            </div>

            <form onSubmit={handleReclamar} className="border p-6" style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(212,175,55,0.25)' }}>
              {error && (
                <div className="mb-4 p-3 text-sm border-l-[3px] border-red-600" style={{ background: 'rgba(192,57,43,0.15)', color: '#e88' }}>
                  {error}
                </div>
              )}
              {[
                { key: 'nombre', label: 'Nombre completo', placeholder: 'Juan Perez' },
                { key: 'email', label: 'Correo electronico', placeholder: 'juan@email.com', type: 'email' },
                { key: 'telefono', label: 'Telefono / WhatsApp', placeholder: '+1 555 123 4567', type: 'tel' },
              ].map(f => (
                <div key={f.key} className="mb-4">
                  <label className="block text-[10px] tracking-wider uppercase text-[#888] mb-1.5" style={{ fontFamily: 'monospace' }}>{f.label}</label>
                  <input
                    type={f.type || 'text'}
                    value={form[f.key]}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    data-testid={`claim-${f.key}`}
                    className="w-full px-4 py-3 text-sm outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: '#FDFAF3' }}
                    onFocus={e => e.target.style.borderColor = '#D4AF37'}
                    onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.12)'}
                  />
                </div>
              ))}
              <div className="mb-5">
                <label className="block text-[10px] tracking-wider uppercase text-[#888] mb-1.5" style={{ fontFamily: 'monospace' }}>Pais</label>
                <select
                  value={form.pais}
                  onChange={e => setForm({ ...form, pais: e.target.value })}
                  data-testid="claim-pais"
                  className="w-full px-4 py-3 text-sm outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: form.pais ? '#FDFAF3' : '#888' }}
                >
                  <option value="">Selecciona tu pais</option>
                  {PAISES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <button
                type="submit"
                disabled={claiming.loading}
                data-testid="claim-submit"
                className="w-full py-4 text-lg font-bold tracking-[2px] cursor-pointer transition-colors disabled:opacity-50"
                style={{ background: '#D4AF37', color: '#1A1A2E', border: 'none' }}
              >
                {claiming.loading ? <><Loader2 className="inline h-5 w-5 animate-spin mr-2" />RECLAMANDO...</> : 'RECLAMAR CODIGO'}
              </button>
              <button
                type="button"
                onClick={() => { setClaiming(null); setError(''); }}
                className="w-full mt-2 py-3 text-sm text-[#888] hover:text-[#FDFAF3] transition-colors"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                Cancelar y volver a los codigos
              </button>
            </form>
          </div>
        ) : (
          /* CODES LIST */
          <div>
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: 'rgba(212,175,55,0.15)', border: '2px solid rgba(212,175,55,0.3)' }}>
                <Gift className="h-7 w-7 text-[#D4AF37]" />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#FDFAF3' }}>Codigos de Viaje Disponibles</h2>
              <p className="text-sm text-[#888]">Selecciona un codigo, ingresa los datos del cliente y reclamalo</p>
              <p className="text-xs text-[#D4AF37] mt-1">Cada codigo vale $500 USD en beneficios hoteleros</p>
            </div>

            {loading ? (
              <div className="text-center py-16">
                <Loader2 className="h-8 w-8 text-[#D4AF37] animate-spin mx-auto mb-3" />
                <p className="text-[#888] text-sm">Cargando codigos...</p>
              </div>
            ) : codigos.length === 0 ? (
              <div className="text-center py-16 border" style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.08)' }}>
                <Users className="h-10 w-10 text-[#888] mx-auto mb-3" />
                {loadError ? (
                  <>
                    <p className="text-[#e88] font-bold mb-2">Error de conexion con el servidor</p>
                    <p className="text-[#888] text-sm mb-4">No pudimos cargar los codigos. El servidor esta temporalmente ocupado.</p>
                    <button
                      onClick={() => { setLoading(true); setLoadError(false); loadCodigos(); }}
                      data-testid="retry-load"
                      className="px-5 py-2 text-sm font-bold tracking-wide cursor-pointer"
                      style={{ background: '#D4AF37', color: '#1A1A2E', border: 'none' }}
                    >
                      Reintentar
                    </button>
                  </>
                ) : (
                  <p className="text-[#888]">Todos los codigos han sido reclamados</p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
                {codigos.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setClaiming(c)}
                    data-testid={`codigo-${c.numero}`}
                    className="group p-3 text-center cursor-pointer transition-all hover:scale-105"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(212,175,55,0.2)' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#D4AF37'; e.currentTarget.style.background = 'rgba(212,175,55,0.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(212,175,55,0.2)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                  >
                    <Gift className="h-5 w-5 text-[#D4AF37] mx-auto mb-1.5 opacity-60 group-hover:opacity-100" />
                    <p className="text-[10px] font-mono text-[#D4AF37] font-bold tracking-wide leading-tight">{c.codigo}</p>
                    <p className="text-[9px] text-[#888] mt-1">$500 USD</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="py-6 text-center text-xs border-t" style={{ background: '#0d0d1e', borderColor: 'rgba(212,175,55,0.25)', color: '#888' }}>
        <p>&copy; 2025 Club Viajemos / Travel Empire — <a href="https://www.clubviajemos.com" className="text-[#D4AF37] no-underline">www.clubviajemos.com</a></p>
      </footer>
    </div>
  );
};

export default CertificadosPage;
